/**
 * ClientProfile — Cert body / admin view of a single client's certification journey.
 * Shows: details, ISO standards being pursued, implementation status, certificates issued.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  PageHeader, Alert, Btn, FormField, StatusBadge,
  selectStyle, colors, spacing, typography, radius,
} from '../design-system';
import axiosClient from '../api/axiosClient';

const BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed',   label: 'Completed' },
];

/* ─── Certification Journey Component ─────────────────────────────────── */

const STAGES = [
  { key: 'stage_1_audit', label: 'Stage 1 Audit',  icon: '📋', desc: 'Document review' },
  { key: 'stage_2_audit', label: 'Stage 2 Audit',  icon: '🔍', desc: 'On-site assessment' },
  { key: 'certification', label: 'Certified',       icon: '🏆', desc: 'ISO certificate issued' },
];

function JourneyTrack({ isoCode, records }) {
  const byType = {};
  records.forEach(r => {
    if (!byType[r.record_type] || new Date(r.issue_date || r.audit_date) > new Date(byType[r.record_type].issue_date || byType[r.record_type].audit_date)) {
      byType[r.record_type] = r;
    }
  });

  const allDone = !!byType['certification'];

  return (
    <div style={{
      border: `1px solid ${allDone ? '#28a745' : colors.border}`,
      borderRadius: radius.lg,
      padding: spacing.md,
      background: allDone ? '#f0fdf4' : colors.surface,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md }}>
        <span style={{ fontWeight: typography.fontWeights.bold, fontSize: typography.fontSizes.md, color: colors.textPrimary }}>
          {isoCode}
        </span>
        {allDone && (
          <span style={{ fontSize: typography.fontSizes.xs, fontWeight: 600, color: '#065f46', background: '#d1fae5', padding: '2px 8px', borderRadius: radius.sm }}>
            CERTIFIED
          </span>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0 }}>
        {STAGES.map((stage, idx) => {
          const rec = byType[stage.key];
          const done = !!rec;
          const isLast = idx === STAGES.length - 1;
          const dateStr = rec
            ? new Date(rec.audit_date || rec.issue_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
            : null;

          return (
            <React.Fragment key={stage.key}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                {/* Circle */}
                <div style={{
                  width: 44, height: 44, borderRadius: '50%', margin: '0 auto',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 20,
                  background: done ? (stage.key === 'certification' ? '#28a745' : '#007bff') : colors.background,
                  border: `2px solid ${done ? (stage.key === 'certification' ? '#28a745' : '#007bff') : colors.border}`,
                  boxShadow: done ? `0 0 0 3px ${stage.key === 'certification' ? '#d1fae5' : '#dbeafe'}` : 'none',
                }}>
                  {done ? (stage.key === 'certification' ? '🏆' : '✓') : (idx + 1)}
                </div>

                {/* Label */}
                <div style={{ marginTop: spacing.xs, fontSize: typography.fontSizes.xs, fontWeight: typography.fontWeights.semibold, color: done ? colors.textPrimary : colors.textMuted }}>
                  {stage.label}
                </div>
                <div style={{ fontSize: '11px', color: colors.textMuted }}>{stage.desc}</div>

                {/* Date or pending */}
                {done ? (
                  <div style={{ fontSize: '11px', color: stage.key === 'certification' ? '#065f46' : '#1e40af', marginTop: '2px', fontWeight: 600 }}>
                    {dateStr}
                    {stage.key === 'certification' && rec.certificate_number && (
                      <div style={{ fontFamily: 'monospace', fontSize: '10px', color: colors.textMuted, marginTop: '1px' }}>
                        {rec.certificate_number}
                      </div>
                    )}
                    {stage.key === 'certification' && rec.expiry_date && (
                      <div style={{ fontSize: '10px', color: colors.textMuted }}>
                        Expires {new Date(rec.expiry_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </div>
                    )}
                    {rec.auditor_name && (
                      <div style={{ fontSize: '10px', color: colors.textMuted }}>by {rec.auditor_name}</div>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '2px' }}>Pending</div>
                )}
              </div>

              {/* Connector line between stages */}
              {!isLast && (
                <div style={{
                  flex: 0, width: 40, height: 2, marginTop: 21,
                  background: byType[STAGES[idx + 1]?.key] || done ? '#007bff' : colors.border,
                  alignSelf: 'flex-start',
                }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function CertificationJourney({ certificates, pursuedStandards, onNavigate }) {
  // Group all records by iso_standard string
  const byStandard = {};
  certificates.forEach(r => {
    const key = r.iso_standard;
    if (!byStandard[key]) byStandard[key] = [];
    byStandard[key].push(r);
  });

  // Standards in progress (have any record) or being pursued with no records yet
  const standardsWithRecords = Object.keys(byStandard);
  const pursuedCodes = pursuedStandards.map(s => s.code);
  const allCodes = [...new Set([...pursuedCodes, ...standardsWithRecords])];

  return (
    <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: spacing.lg, marginTop: spacing.lg }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md }}>
        <h3 style={{ margin: 0, fontSize: typography.fontSizes.lg, color: colors.textPrimary }}>
          Certification Journey
        </h3>
        <Btn size="sm" onClick={onNavigate}>+ Log Audit / Issue Cert</Btn>
      </div>

      {allCodes.length === 0 ? (
        <p style={{ color: colors.textMuted }}>No ISO standards selected or audit records logged yet. Select standards above and log a Stage 1 Audit to begin.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
          {allCodes.map(code => (
            <JourneyTrack key={code} isoCode={code} records={byStandard[code] || []} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */

export default function ClientProfile() {
  const { clientId } = useParams();
  const navigate     = useNavigate();
  const token        = localStorage.getItem('token');
  const headers      = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [client, setClient]           = useState(null);
  const [certificates, setCerts]      = useState([]);
  const [allStandards, setStandards]  = useState([]);
  const [loading, setLoading]         = useState(true);
  const [msg, setMsg]                 = useState('');
  const [msgType, setMsgType]         = useState('success');
  const [saving, setSaving]           = useState(false);
  const [selectedStandards, setSelectedStandards] = useState([]);
  const [status, setStatus]           = useState('not_started');

  const flash = (m, t = 'success') => { setMsg(m); setMsgType(t); setTimeout(() => setMsg(''), 4000); };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [clientRes, certsRes, standardsRes] = await Promise.all([
        fetch(`${BASE}/clients/${clientId}`, { headers }),
        axiosClient.get('/certifications/certifications/', { params: { company_id: clientId } }),
        axiosClient.get('/iso-standards/purchased'),
      ]);
      if (!clientRes.ok) throw new Error('Client not found');
      const clientData = await clientRes.json();
      setClient(clientData);
      setSelectedStandards(Array.isArray(clientData.iso_standards) ? clientData.iso_standards : []);
      setStatus(clientData.implementation_status || 'not_started');
      setCerts(Array.isArray(certsRes.data) ? certsRes.data.filter(c => c.company_id === parseInt(clientId)) : []);
      setStandards(Array.isArray(standardsRes.data) ? standardsRes.data : []);
    } catch (err) {
      flash('Failed to load client: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleToggleStandard = (id) => {
    setSelectedStandards(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSaveStandards = async () => {
    setSaving(true);
    try {
      await fetch(`${BASE}/clients/${clientId}/iso-standards`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ iso_standard_ids: selectedStandards }),
      });
      flash('ISO standards updated');
    } catch {
      flash('Failed to save standards', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveStatus = async () => {
    setSaving(true);
    try {
      await fetch(`${BASE}/clients/${clientId}/implementation-status`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ status }),
      });
      flash('Implementation status updated');
    } catch {
      flash('Failed to update status', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: spacing.xl }}>Loading...</div>;
  if (!client) return <div style={{ padding: spacing.xl }}>Client not found.</div>;

  const statusColor = { not_started: colors.textMuted, in_progress: colors.warning, completed: colors.success };

  return (
    <div>
      <PageHeader
        title={client.name}
        subtitle={[client.industry, client.size, client.country].filter(Boolean).join(' · ')}
        actions={<Btn variant="secondary" onClick={() => navigate('/clients')}>← Back to Clients</Btn>}
      />

      {msg && <Alert type={msgType} message={msg} onClose={() => setMsg('')} />}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.lg, marginTop: spacing.lg }}>

        {/* ── Implementation Status ── */}
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: spacing.lg }}>
          <h3 style={{ margin: `0 0 ${spacing.md}`, fontSize: typography.fontSizes.lg, color: colors.textPrimary }}>
            Implementation Status
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.md }}>
            <div style={{
              width: 12, height: 12, borderRadius: '50%',
              background: statusColor[status] || colors.textMuted,
            }} />
            <span style={{ fontSize: typography.fontSizes.md, color: colors.textPrimary, fontWeight: typography.fontWeights.semibold }}>
              {STATUS_OPTIONS.find(o => o.value === status)?.label || status}
            </span>
          </div>
          <FormField label="Update Status">
            <select value={status} onChange={e => setStatus(e.target.value)} style={selectStyle}>
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </FormField>
          <Btn onClick={handleSaveStatus} loading={saving} style={{ marginTop: spacing.sm }}>
            Save Status
          </Btn>
        </div>

        {/* ── Client Info ── */}
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: spacing.lg }}>
          <h3 style={{ margin: `0 0 ${spacing.md}`, fontSize: typography.fontSizes.lg, color: colors.textPrimary }}>
            Company Details
          </h3>
          {[
            ['Industry', client.industry],
            ['Size', client.size],
            ['Country', client.country],
            ['Website', client.website],
            ['Contact Email', client.contact_email],
            ['Users', client.users?.length ?? 0],
          ].map(([label, value]) => value ? (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: `${spacing.xs} 0`, borderBottom: `1px solid ${colors.border}` }}>
              <span style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted }}>{label}</span>
              <span style={{ fontSize: typography.fontSizes.sm, color: colors.textPrimary }}>{value}</span>
            </div>
          ) : null)}
        </div>
      </div>

      {/* ── ISO Standards Being Pursued ── */}
      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: spacing.lg, marginTop: spacing.lg }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md }}>
          <h3 style={{ margin: 0, fontSize: typography.fontSizes.lg, color: colors.textPrimary }}>
            ISO Standards Being Pursued
          </h3>
          <Btn size="sm" onClick={handleSaveStandards} loading={saving}>Save</Btn>
        </div>
        {allStandards.length === 0 ? (
          <p style={{ color: colors.textMuted }}>No ISO standards available.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: spacing.sm }}>
            {allStandards.map(s => {
              const active = selectedStandards.includes(s.id);
              return (
                <label key={s.id} style={{
                  display: 'flex', alignItems: 'center', gap: spacing.sm,
                  padding: spacing.sm, borderRadius: radius.md, cursor: 'pointer',
                  border: `1px solid ${active ? colors.primary : colors.border}`,
                  background: active ? colors.primaryLight : colors.background,
                }}>
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={() => handleToggleStandard(s.id)}
                    style={{ accentColor: colors.primary }}
                  />
                  <div>
                    <div style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, color: colors.textPrimary }}>
                      {s.code}
                    </div>
                    <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted }}>
                      {s.name}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Certification Journey ── */}
      <CertificationJourney
        certificates={certificates}
        pursuedStandards={allStandards.filter(s => selectedStandards.includes(s.id))}
        onNavigate={() => navigate('/iso-certificates')}
      />

      {/* ── Users ── */}
      {client.users?.length > 0 && (
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: spacing.lg, marginTop: spacing.lg }}>
          <h3 style={{ margin: `0 0 ${spacing.md}`, fontSize: typography.fontSizes.lg, color: colors.textPrimary }}>Users</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: colors.background, borderBottom: `2px solid ${colors.border}` }}>
                {['Name', 'Email', 'Username', 'Role'].map(h => (
                  <th key={h} style={{ padding: spacing.sm, textAlign: 'left', fontSize: typography.fontSizes.sm, color: colors.textMuted }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {client.users.map(u => (
                <tr key={u.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <td style={{ padding: spacing.sm, fontSize: typography.fontSizes.sm }}>{u.full_name || '—'}</td>
                  <td style={{ padding: spacing.sm, fontSize: typography.fontSizes.sm }}>{u.email}</td>
                  <td style={{ padding: spacing.sm, fontSize: typography.fontSizes.sm, fontFamily: 'monospace' }}>{u.username}</td>
                  <td style={{ padding: spacing.sm, fontSize: typography.fontSizes.sm }}>{u.role}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
